import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/SubscriptionsService';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

export const useSubscriptions = () => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});
    const { id: customerId } = useParams();
    
    const sort = { column: 'created', direction: 'desc' as const };
    const filter = customerId ? { customerId } : undefined;
    
    // INFINITE QUERY
    const subscriptionsQuery = useInfiniteQuery({
        queryKey: ['subscriptions', keyword, limit, sort.column, sort.direction, customerId],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({ limit, sort, filter, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
        }
    });

    const fetchRecordsAction = () => {
        if (subscriptionsQuery.hasNextPage) {
            subscriptionsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = subscriptionsQuery.data?.pages?.flatMap(page => page && 'items' in page ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: subscriptionsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: subscriptionsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};